import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { action, setProperties } from '@ember/object';
import { ALL_CHANNELS } from 'cio/utils/metrics/common';
import { actionTypeName } from 'cio/utils/action-info';

import { ActionType } from 'cio/models/action';
import { SortDirection } from 'cio/utils/sort';
import EnvironmentService from 'cio/services/environment';

export enum SortProperty {
  Name = 'name',
  Status = 'status',
  UpdatedAt = 'updated_at',
  UsedIn = 'used_in',
}

export enum StatusesFilter {
  Active = 'active',
  Automatic = 'automatic',
  Draft = 'draft',
  Drafted = 'drafted',
  Off = 'off',
  Sent = 'sent',
}

export const ALL_STATUSES_FILTERS = Object.freeze([
  StatusesFilter.Active,
  StatusesFilter.Automatic,
  StatusesFilter.Draft,
  StatusesFilter.Drafted,
  StatusesFilter.Off,
  StatusesFilter.Sent,
]);

export const DEFAULT_VALUES = {
  excludeArchived: false,
  page: 1,
  query: '',
  sortBy: SortProperty.UpdatedAt,
  sortDirection: SortDirection.Descending,
  statuses: [...ALL_STATUSES_FILTERS],
};

export default class MessageLibraryIndexController extends Controller {
  @service environment!: EnvironmentService;

  queryParams = ['channels', 'excludeArchived', 'page', 'query', 'sortBy', 'sortDirection', 'statuses'];

  @tracked channels: string[] = [];
  @tracked excludeArchived = DEFAULT_VALUES.excludeArchived;
  @tracked page = DEFAULT_VALUES.page;
  @tracked query = DEFAULT_VALUES.query;
  @tracked sortBy: SortProperty = DEFAULT_VALUES.sortBy;
  @tracked sortDirection = DEFAULT_VALUES.sortDirection;
  @tracked statuses = DEFAULT_VALUES.statuses;

  get availableChannelActionTypes() {
    return ALL_CHANNELS.map((channel) => `${channel}_action`).filter((type: ActionType) =>
      this.environment.enabledAction(type),
    );
  }
  get availableChannelActionTypeNames() {
    return this.availableChannelActionTypes.map((type: ActionType) => actionTypeName(type));
  }

  get defaultValues() {
    return {
      channels: this.availableChannelActionTypeNames,
      excludeArchived: DEFAULT_VALUES.excludeArchived,
      page: DEFAULT_VALUES.page,
      query: DEFAULT_VALUES.query,
      sortBy: DEFAULT_VALUES.sortBy,
      sortDirection: DEFAULT_VALUES.sortDirection,
    };
  }

  setAllAvailableChannels() {
    if (this.channels.length !== 0) {
      return undefined;
    }

    return (this.channels = this.availableChannelActionTypeNames);
  }

  setDefaults() {
    setProperties(this, this.defaultValues);
  }

  @action
  setQuery(query: string) {
    this.page = DEFAULT_VALUES.page;
    this.query = query;
  }

  @action
  setSort(sortBy: SortProperty, sortDirection: SortDirection) {
    this.page = DEFAULT_VALUES.page;
    this.sortBy = sortBy;
    this.sortDirection = sortDirection;
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your controllers.
declare module '@ember/controller' {
  interface Registry {
    'message-library': MessageLibraryIndexController;
  }
}
